/* ============================================================
   Pink Stork — Mobile Header Behavior + Widget Close Buttons
   Build: v26.12.9 — 2026-04-22
   Loaded once from layout/theme.liquid before </body>.

   Two responsibilities:
   (1) Toggle `body.ps-scrolled` on vertical scroll past 80px so
       CSS can collapse the mobile header (hide sub-nav, shrink
       logo, hide tagline) once the customer starts scrolling.
   (2) Inject close (X) buttons on the live-chat widget and the
       accessibility widget (Gorgias / Zendesk / accessiBe /
       UserWay / generic), so customers can dismiss them. We
       remember the dismissed state in sessionStorage so they
       stay closed for the visit but reappear on a fresh tab.
   ============================================================ */

(function () {
  'use strict';

  /* ─── (1) Scroll-class toggle ────────────────────────────── */
  var ticking = false;
  var threshold = 40; // px past which the header collapses (kept low so
                      // the collapse feels instant — Thomas's reference is
                      // any scroll = collapsed, not "after scrolling far")

  function updateScrollClass() {
    var y = window.pageYOffset || document.documentElement.scrollTop;
    var scrolled = y > threshold;
    document.body.classList.toggle('ps-scrolled', scrolled);
    // Debug attribute so we can confirm via DevTools that the toggle ran
    document.body.setAttribute('data-ps-scroll', String(Math.round(y)));
    ticking = false;
  }

  function onScroll() {
    if (!ticking) {
      window.requestAnimationFrame(updateScrollClass);
      ticking = true;
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  updateScrollClass(); // run once on load

  /* ─── (2) Widget close buttons ──────────────────────────── */
  // Selectors for known chat + accessibility widget root elements.
  // We probe each one and attach a close (X) button if present.
  // v26.73 — Restored accessibility widgets to the scan list. The
  // earlier removal left zero X (the widget didn't have its own native
  // close after all). Now we inject ONE X, and use a deduplication
  // step below to ensure only one X exists per widget at any time.
  var WIDGETS = [
    { key: 'gorgias',     selectors: ['#gorgias-chat-container', '.gorgias-chat-container', 'iframe[id^="chat-button"]'], label: 'Close chat' },
    { key: 'zendesk',     selectors: ['iframe#launcher', 'iframe[title*="Messaging"]', '.zEWidget-launcher'], label: 'Close chat' },
    { key: 'intercom',    selectors: ['.intercom-launcher', '.intercom-lightweight-app-launcher', '#intercom-container'], label: 'Close chat' },
    { key: 'tidio',       selectors: ['#tidio-chat', '#tidio-chat-iframe'], label: 'Close chat' },
    { key: 'crisp',       selectors: ['.crisp-client'], label: 'Close chat' },
    { key: 'shopify-inbox', selectors: ['#ShopifyChat', 'iframe[name="ShopifyChat"]', '.shopify-chat-launcher'], label: 'Close chat' },
    { key: 'accessibly',  selectors: ['#accessiblyAppWidgetButton', '#accessiblyAppDialogButton', '.accessibly-app-widget-button', '.accessibly-app-widget-button-container'], label: 'Close accessibility menu' },
    { key: 'accessibe',   selectors: ['.acsb-trigger', '#acsb-icon', '[data-acsb-custom-trigger="true"]'], label: 'Close accessibility menu' },
    { key: 'userway',     selectors: ['#userwayAccessibilityIcon', '.userway_buttons_wrapper', 'iframe[title*="Accessibility"]'], label: 'Close accessibility menu' },
    { key: 'equalweb',    selectors: ['#INDmenu-btn', '.INDmenu-btn'], label: 'Close accessibility menu' },
    { key: 'audioeye',    selectors: ['#ae_launcher', '.ae-launcher'], label: 'Close accessibility menu' }
  ];

  function dismissedKey(key) { return 'ps-dismiss-' + key; }

  function isDismissed(key) {
    try { return sessionStorage.getItem(dismissedKey(key)) === '1'; }
    catch (e) { return false; }
  }

  function setDismissed(key) {
    try { sessionStorage.setItem(dismissedKey(key), '1'); } catch (e) {}
  }

  function attachCloseButton(widgetEl, label, key) {
    if (!widgetEl || widgetEl.dataset.psCloseAttached === '1') return;
    // v26.73 — Dedupe. If another ps-widget-close already exists for this widget
    // key (e.g. multiple selectors matched the same widget root via different
    // children), don't add another one.
    if (document.querySelector('.ps-widget-close[data-ps-key="' + key + '"]')) return;
    widgetEl.dataset.psCloseAttached = '1';

    // The widget element itself is sometimes an iframe living in the
    // page. We anchor an absolutely-positioned close button next to it
    // by inserting a sibling wrapper.
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ps-widget-close';
    btn.setAttribute('aria-label', label);
    btn.setAttribute('data-ps-key', key);
    btn.innerHTML = '\u00D7'; // ×

    // Position the button at the top-right corner of the widget's
    // bounding box, fixed so it tracks the widget on scroll.
    function positionButton() {
      var rect = widgetEl.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        btn.style.display = 'none';
        return;
      }
      btn.style.display = '';
      btn.style.top = Math.max(0, rect.top - 6) + 'px';
      btn.style.left = Math.min(window.innerWidth - 30, rect.right - 18) + 'px';
    }

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      // Hide all matching elements for this widget key
      document.querySelectorAll('[data-ps-widget-key="' + key + '"]').forEach(function (el) {
        el.style.display = 'none';
      });
      // Also hide the widget element itself even if it doesn't have the marker
      try { widgetEl.style.setProperty('display', 'none', 'important'); } catch (ex) {}
      btn.remove();
      setDismissed(key);
    });

    widgetEl.dataset.psWidgetKey = key;
    document.body.appendChild(btn);
    positionButton();
    window.addEventListener('resize', positionButton, { passive: true });
    window.addEventListener('scroll', positionButton, { passive: true });
  }

  function scanWidgets() {
    WIDGETS.forEach(function (w) {
      if (isDismissed(w.key)) {
        // Hide it on subsequent loads within the session
        w.selectors.forEach(function (sel) {
          document.querySelectorAll(sel).forEach(function (el) {
            try { el.style.setProperty('display', 'none', 'important'); } catch (e) {}
          });
        });
        return;
      }
      w.selectors.forEach(function (sel) {
        var matches = document.querySelectorAll(sel);
        matches.forEach(function (el) {
          attachCloseButton(el, w.label, w.key);
        });
      });
    });
  }

  // Widgets load asynchronously. Poll for ~30 seconds after page load,
  // then keep a slow MutationObserver running for late-arriving widgets
  // (e.g., third-party scripts that wait for user interaction).
  var fastChecks = 0;
  var fastTimer = setInterval(function () {
    scanWidgets();
    fastChecks += 1;
    if (fastChecks > 30) clearInterval(fastTimer); // 30 × 1s = 30s
  }, 1000);

  if (window.MutationObserver) {
    var slowObserver = new MutationObserver(function () {
      // Debounce — don't run on every DOM tick
      if (slowObserver._timer) return;
      slowObserver._timer = setTimeout(function () {
        slowObserver._timer = null;
        scanWidgets();
      }, 800);
    });
    slowObserver.observe(document.body, { childList: true, subtree: true });
  }
})();
